import PublicAPI from '@/api/TreedisPublicDAO';
import AdminAPI from '@/api/TreedisAdminDAO';
import { TourImage, TourObject, TourVideo, TourVSHtml } from '@/_models/virtualStaging/VSObject';
import { VSSettings } from '@/_models/virtualStaging/VSSettings';
import { Polygon } from '@/_models/virtualStaging/Polygon';
import { getQueryVariable } from '@/components/util';
import { TCurrentSweep } from '@/components/tour/game/GameplayScreen/types';

export const getFloorPlans = async (id: number) => {
  const res = await PublicAPI.getFloorPlans({ id });

  return res?.floorPlans || [];
};

export const getDuplicates = async (id: number) => {
  const res = await PublicAPI.getDuplicates(id);
  return res?.duplicates || [];
};

export const getPolygons = async (slug: string) => {
  const res = await AdminAPI.getPolygons(slug);
  return res?.data;
};

export const getSweeps = async (matterId: string, slug: string) => {
  if (!matterId) return [];

  const res = await PublicAPI.getSweeps({
    matterId,
    slug,
    auth: getQueryVariable(window.location.href, 'auth'),
  });

  return res?.sweeps || [];
};

export const getSweepIndex = async (matterId: string, slug: string, sweepId: string) => {
  if (!matterId || !sweepId) return -1;

  const res = await PublicAPI.getSweepIndex({
    matterId,
    slug,
    sweepId,
    auth: getQueryVariable(window.location.href, 'auth'),
  });

  return res?.data?.sweepIndex + 1 || -1;
};

export const getPath = async (sweeps: TCurrentSweep[], startSweep: string, endSweep: string, tourId: number, navigationType?: string) => {
  const res = await PublicAPI.getPath({
    sweeps,
    startSweep,
    endSweep,
    tourId,
    navigationType,
  });
  return res?.data;
};

export const getNavigationSweeps = async (id?: number) => {
  const response = await AdminAPI.getNavigationSweeps({
    query: {
      id,
    },
  });
  return response.navigationSweeps;
};

export const getTourVSObjects = async (id: string | number) => {
  const res = await AdminAPI.getTourVSObjects(id);
  return res?.tourVSObjects;
};

export const getTourVSImages = async (slug: string) => {
  const res = await AdminAPI.getTourVSImages(slug);
  return res?.tourVSImages;
};

export const getTourVSVideos = async (slug: string) => {
  const res = await AdminAPI.getTourVSVideos(slug);
  return res?.data;
};

export const getTourVSHtmls = async (slug: string) => {
  const res = await AdminAPI.getTourVSHtmls(slug);
  return res?.data;
};

export const getBarriers = async (id: string | number) => {
  const res = await AdminAPI.getBarriers({
    query: {
      id,
    },
  });
  return res?.barriers;
};

export const getTourVSSettings = async (id: string | number) => {
  const res = await AdminAPI.getVSSettings(id);
  return res?.vsSettings;
};

export const getVSItems = async (isMatterportLink, { slug, id }: any) => {
  const [polygons, vsObjectsRes, vsImages, vsSettings, tourVideosData, vsHtmls] = await Promise.all([
    !isMatterportLink ? getPolygons(slug) : Promise.resolve(null),
    !isMatterportLink ? getTourVSObjects(id) : Promise.resolve(null),
    !isMatterportLink ? getTourVSImages(slug) : Promise.resolve(null),
    !isMatterportLink ? getTourVSSettings(id) : Promise.resolve(null),
    !isMatterportLink ? getTourVSVideos(slug) : Promise.resolve(null),
    !isMatterportLink ? getTourVSHtmls(slug) : Promise.resolve(null),
  ]);

  const tourVideos: Array<TourVideo> = (tourVideosData || []).map((item: any) => new TourVideo(item));
  const tourImages: Array<TourImage> = vsImages?.map((item: any) => new TourImage(item)) || [];
  const tourHtmls: Array<TourVSHtml> = vsHtmls?.map((item: any) => new TourVSHtml(item)) || [];

  const vsObjects = (vsObjectsRes || [])
    .map((item: TourObject) => new TourObject(item))
    .concat(tourImages)
    .concat(tourHtmls)
    .concat(tourVideos);

  return {
    polygons: polygons ? polygons.map((polygon) => new Polygon(polygon)) : [],
    vsSettings: new VSSettings(vsSettings || {}),
    vsObjects,
  };
};

export const getAllMenuItems = async (id: string | number) => {
  const res = await AdminAPI.getAllMenuItems({
    query: {
      id,
    },
  });
  return res.navigationItems;
};

export const getMenuCategories = async (id: string | number) => {
  const res = await AdminAPI.getMenuCategories({
    query: {
      id,
    },
  });
  return res.navigationCategories;
};

export const getNaviationSettings = async (id: string | number) => {
  const res = await AdminAPI.getNaviationSettings({
    query: {
      id,
    },
  });
  return res.navigationSettings;
};

export const getNavigationFinishScreen = async (id: string | number) => {
  const res = await AdminAPI.getNavigationFinishScreen({
    query: {
      id,
    },
  });
  return res.finishScreen;
};

export const getNavigationData = async (id: number) => {
  const [navigationItems, navigationCategories, navigationSettings, navigationFinishScreen] = await Promise.all([
    getAllMenuItems(id),
    getMenuCategories(id),
    getNaviationSettings(id),
    getNavigationFinishScreen(id),
  ]);

  return { navigationItems, navigationCategories, navigationSettings, navigationFinishScreen };
};
